import { cn } from "@/lib/utils";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: string;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: "" };
  }

  static getDerivedStateFromError(error: unknown): Partial<State> {
    // Handle non-Error throws (strings, objects, null) which can happen in production React
    if (error instanceof Error) {
      return { hasError: true, error };
    }
    const syntheticError = new Error(String(error));
    syntheticError.name = typeof error === 'object' ? 'NonErrorThrow' : typeof error;
    return { hasError: true, error: syntheticError };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Capture the component stack from React
    const info = [
      `Name: ${error?.name || "Unknown"}`,
      `Message: ${error?.message || "(empty)"}`,
      `toString: ${String(error)}`,
      `Component Stack: ${errorInfo?.componentStack || "(none)"}`,
    ].join("\n");
    this.setState({ errorInfo: info });
  }

  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      const errStr = err ? String(err) : "Unknown error";
      const errMsg = err?.message || errStr;
      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-background">
          <div className="flex flex-col items-center w-full max-w-2xl p-8">
            <AlertTriangle
              size={48}
              className="text-destructive mb-6 flex-shrink-0"
            />

            <h2 className="text-xl mb-4">An unexpected error occurred.</h2>

            <p className="text-sm font-medium text-destructive mb-3 text-center break-all">
              {errMsg}
            </p>

            {this.state.errorInfo && (
              <div className="p-3 w-full rounded bg-amber-50 border border-amber-200 overflow-auto mb-4 max-h-40">
                <pre className="text-xs text-amber-800 whitespace-pre-wrap">
                  {this.state.errorInfo}
                </pre>
              </div>
            )}

            <div className="p-4 w-full rounded bg-muted overflow-auto mb-6 max-h-60">
              <pre className="text-sm text-muted-foreground whitespace-break-spaces">
                {err?.stack}
              </pre>
            </div>

            <button
              onClick={() => window.location.reload()}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg",
                "bg-primary text-primary-foreground",
                "hover:opacity-90 cursor-pointer"
              )}
            >
              <RotateCcw size={16} />
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
